// ReplaceCoveragePanel — lost cover on a submitted request ("Choose another
// delegation"). Shown when the delegation linked to a pending / approved,
// not-yet-over request was declined or cancelled (or ran out before the leave
// began). Lists the requester's own delegations covering every day of that
// leave (the same picker and rule as the leave form) and attaches the chosen
// one with PUT /leave-requests/:id/delegation — validated server-side.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/apiError';
import { replaceLeaveDelegation } from '@/lib/hrApi';
import { getDelegations, type Delegation } from '@/services/rbacService';
import { Button } from '@/features/shared/components/ui/button';
import { ExistingDelegationPicker, type ExistingLoadState } from './ExistingDelegationPicker';
import { existingDelegationOptions, localCivilToday, preferredExisting } from './handover';

const DEAD = new Set(['declined', 'revoked', 'rejected']);

/** Does this request's cover need replacing? Mirrors leave-service
 *  delegation_link.replacement_state. */
export function coverNeedsReplacing(r: {
  status: string;
  startDate: string;
  endDate?: string | null;
  coverageStatus?: string | null;
}, today: string = localCivilToday()): boolean {
  if (!['pending', 'approved'].includes(r.status)) return false;
  if ((r.endDate || r.startDate) < today) return false;
  const s = String(r.coverageStatus ?? '');
  if (DEAD.has(s)) return true;
  return s === 'expired' && r.startDate > today;
}

export interface ReplaceCoveragePanelProps {
  requestId: string;
  startDate: string;
  endDate: string;
  onReplaced: () => void;
  /** Open the delegations page to create a new one. */
  onCreateNew?: () => void;
}

export const ReplaceCoveragePanel: React.FC<ReplaceCoveragePanelProps> = ({ requestId, startDate, endDate, onReplaced, onCreateNew }) => {
  const { t } = useTranslation(['timeoff']);
  const [mine, setMine] = useState<Delegation[] | null>(null);
  const [state, setState] = useState<ExistingLoadState>('loading');
  const [nonce, setNonce] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setState('loading');
    getDelegations()
      .then((m) => { if (live) { setMine(m.asDelegator); setState('ready'); } })
      .catch(() => { if (live) setState('error'); });
    return () => { live = false; };
  }, [nonce]);

  const options = useMemo(() => existingDelegationOptions(mine, startDate, endDate, requestId), [mine, startDate, endDate, requestId]);
  useEffect(() => {
    if (!chosen && state === 'ready') setChosen(preferredExisting(options.eligible)?.delegation.id ?? null);
  }, [chosen, state, options]);

  const attach = async () => {
    if (!chosen) return;
    setSaving(true);
    try {
      await replaceLeaveDelegation(requestId, chosen);
      toast.success(t('timeoff:replace_done', { defaultValue: 'Your time off has a delegate again.' }));
      onReplaced();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('timeoff:replace_failed', { defaultValue: "Couldn't attach that delegation." })));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-border bg-warning-soft p-2.5" data-testid="replace-coverage">
      <p className="m-0 text-xs font-semibold text-warning-strong">
        {t('timeoff:replace_needed', { defaultValue: 'This time off has no cover any more. Choose another delegation.' })}
      </p>
      <ExistingDelegationPicker
        state={state}
        eligible={options.eligible}
        ineligible={options.ineligible}
        selectedId={chosen}
        onSelect={setChosen}
        onRetry={() => setNonce((n) => n + 1)}
        onCreateNew={onCreateNew}
        startDate={startDate}
        endDate={endDate}
      />
      {state === 'ready' && options.eligible.length > 0 && (
        <Button size="sm" onClick={attach} disabled={!chosen || saving} data-testid="replace-attach">
          {saving ? t('timeoff:replace_saving', { defaultValue: 'Attaching…' }) : t('timeoff:replace_attach', { defaultValue: 'Use this delegation' })}
        </Button>
      )}
    </div>
  );
};

export default ReplaceCoveragePanel;
